import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { endpointAPI, createEndpointWs } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';
import PtyTerminal from '../servers/components/PtyTerminal';

interface Endpoint {
  id: number;
  endpoint_id: string;
  name: string;
  os: string;
  os_version?: string;
  os_build?: string;
  hostname?: string;
  make?: string;
  model?: string;
  serial_number?: string;
  cpu_model?: string;
  cpu_cores?: number;
  ram_gb?: number;
  storage_gb?: number;
  primary_ip?: string;
  mac_address?: string;
  agent_version?: string;
  status: string;
  last_seen_at?: string;
  enrolled_at?: string;
  inventory?: {
    gpu?: string;
    bios_version?: string;
    disks?: Array<{ mount: string; total_gb: number; used_gb: number; percent: number }>;
    installed_apps?: string[];
    network_interfaces?: Array<{ name: string; mac?: string; ipv4?: string }>;
    collected_at?: string;
  } | null;
  current_metrics?: any;
}

interface Command {
  id: number;
  command_type: string;
  status: string;
  result?: string;
  exit_code?: number;
  created_at: string;
  completed_at?: string;
}

interface Threat {
  id: number;
  file_path: string;
  signature_name?: string;
  severity: string;
  source: string;
  action: string;
  status: string;
  detected_at: string;
}

interface Scan {
  id: number;
  scan_type: string;
  trigger: string;
  status: string;
  files_scanned: number;
  threats_found: number;
  completed_at?: string;
  created_at: string;
}

const gauge = (label: string, value: number | null | undefined, unit = '%') => {
  const v = value != null ? Math.round(value) : null;
  const color = v == null ? '#9ca3af' : v >= 90 ? '#dc2626' : v >= 75 ? '#d97706' : '#16a34a';
  return (
    <div className="col-6 col-md-3">
      <div className="card h-100 text-center">
        <div className="card-body">
          <div className="fs-2 fw-bold" style={{ color }}>{v != null ? `${v}${unit}` : '—'}</div>
          <div className="text-muted small">{label}</div>
          {v != null && unit === '%' && (
            <div className="progress mt-2" style={{ height: 5 }}>
              <div className="progress-bar" style={{ width: `${v}%`, backgroundColor: color }}></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EndpointDetailPage: React.FC = () => {
  useDocumentTitle('Endpoint');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [ep, setEp] = useState<Endpoint | null>(null);
  const [commands, setCommands] = useState<Command[]>([]);
  const [threats, setThreats] = useState<Threat[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [usage, setUsage] = useState<{ total_seconds: number; top_apps: any[]; top_sites: any[] } | null>(null);
  const [usageEnabled, setUsageEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTerminal, setShowTerminal] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [terminalFullscreen, setTerminalFullscreen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const ptyHandlerRef = useRef<((msg: any) => void) | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      const [detailRes, cmdRes, threatRes, scanRes, usageRes, policyRes] = await Promise.all([
        endpointAPI.get(Number(id)),
        endpointAPI.listCommands(Number(id)),
        endpointAPI.listThreats(Number(id), 'open'),
        endpointAPI.listScans(Number(id)),
        endpointAPI.getUsage(Number(id), 7),
        endpointAPI.getPolicy().catch(() => null),
      ]);
      if (detailRes.data.status === 'success') setEp(detailRes.data.data);
      if (cmdRes.data.status === 'success') setCommands(cmdRes.data.data.commands || []);
      if (threatRes.data.status === 'success') setThreats(threatRes.data.data.threats || []);
      if (scanRes.data.status === 'success') setScans(scanRes.data.data.scans || []);
      if (usageRes.data.status === 'success') setUsage(usageRes.data.data);
      if (policyRes?.data?.status === 'success') setUsageEnabled(!!policyRes.data.data.usage_tracking_enabled);
    } catch (error: any) {
      if (error.response?.status === 404) { toast.error('Endpoint not found'); navigate('/endpoints'); }
    } finally {
      setLoading(false);
    }
  };

  const fmtDuration = (secs: number): string => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m`;
    return `${secs}s`;
  };

  const actOnThreat = async (threatId: number, status: string, action?: string, label?: string) => {
    try {
      await endpointAPI.updateThreat(Number(id), threatId, { status, action });
      toast.success(label || 'Threat updated');
      load();
    } catch {
      toast.error('Failed to update threat');
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    pollRef.current = setInterval(load, 10000); // live view: refresh every 10s
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Open the live-shell WebSocket only when the terminal is shown.
  useEffect(() => {
    if (!showTerminal || !id) return;
    const ws = createEndpointWs(Number(id));
    if (!ws) { toast.error('Not authenticated for live shell'); return; }
    wsRef.current = ws;
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'agent_status') { setWsConnected(msg.payload?.connected ?? false); return; }
        ptyHandlerRef.current?.(msg);
      } catch { /* ignore */ }
    };
    return () => { ws.close(); wsRef.current = null; setWsConnected(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTerminal, id]);

  const runCommand = async (cmd: string, label: string, confirmMsg?: string) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await endpointAPI.issueCommand(Number(id), cmd);
      toast.success(`${label} queued`);
      load();
    } catch {
      toast.error(`Failed to queue ${label.toLowerCase()}`);
    }
  };

  const removeEndpoint = async () => {
    if (!window.confirm('Remove this endpoint? The agent will stop reporting once uninstalled.')) return;
    try {
      await endpointAPI.remove(Number(id));
      toast.success('Endpoint removed');
      navigate('/endpoints');
    } catch { toast.error('Failed to remove endpoint'); }
  };

  if (loading || !ep) {
    return (
      <DashboardLayout>
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      </DashboardLayout>
    );
  }

  const m = ep.current_metrics;
  const online = ep.status === 'online';
  const statusColor = online ? 'success' : ep.status === 'pending' ? 'warning' : 'secondary';

  const infoRows: Array<[string, string | number | undefined]> = [
    ['Make', ep.make],
    ['Model', ep.model],
    ['Serial number', ep.serial_number],
    ['Operating system', [ep.os, ep.os_version].filter(Boolean).join(' ') || undefined],
    ['OS build', ep.os_build],
    ['CPU', ep.cpu_model],
    ['CPU cores', ep.cpu_cores],
    ['RAM', ep.ram_gb ? `${ep.ram_gb} GB` : undefined],
    ['Storage', ep.storage_gb ? `${ep.storage_gb} GB` : undefined],
    ['GPU', ep.inventory?.gpu],
    ['BIOS', ep.inventory?.bios_version],
    ['Hostname', ep.hostname],
    ['IP address', ep.primary_ip],
    ['MAC address', ep.mac_address],
    ['Agent version', ep.agent_version],
  ];

  return (
    <DashboardLayout>
      <div className="container-fluid py-3">
        <button className="btn btn-link px-0 mb-2" onClick={() => navigate('/endpoints')}>
          <i className="bi bi-arrow-left me-1"></i>Back to Endpoints
        </button>

        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
          <div>
            <h3 className="mb-1">
              <i className="bi bi-pc-display me-2"></i>{ep.name}
              <span className={`badge bg-${statusColor} ms-2 align-middle`} style={{ fontSize: '0.5em' }}>
                {ep.status.toUpperCase()}
              </span>
            </h3>
            <p className="text-muted mb-0">
              {ep.make || 'Unknown'} {ep.model || ''} · Last seen {ep.last_seen_at ? new Date(ep.last_seen_at).toLocaleString() : 'never'}
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-outline-primary btn-sm" disabled={!online}
              onClick={() => runCommand('reboot', 'Reboot', 'Reboot this endpoint now?')}>
              <i className="bi bi-arrow-clockwise me-1"></i>Reboot
            </button>
            <button className="btn btn-outline-primary btn-sm" disabled={!online}
              onClick={() => runCommand('system_update', 'System update')}>
              <i className="bi bi-download me-1"></i>Update
            </button>
            <button className="btn btn-outline-primary btn-sm" disabled={!online}
              onClick={() => runCommand('run_scan', 'Scan')}>
              <i className="bi bi-shield-check me-1"></i>Scan
            </button>
            <button className={`btn btn-sm ${showTerminal ? 'btn-dark' : 'btn-outline-dark'}`} disabled={!online}
              onClick={() => setShowTerminal(v => !v)}>
              <i className="bi bi-terminal me-1"></i>{showTerminal ? 'Hide Shell' : 'Live Shell'}
            </button>
            <button className="btn btn-outline-danger btn-sm" onClick={removeEndpoint}>
              <i className="bi bi-trash me-1"></i>Remove
            </button>
          </div>
        </div>

        {/* Live metrics */}
        <h6 className="text-muted text-uppercase small mb-2">Live Health <span className="badge bg-light text-dark ms-1">auto-refresh</span></h6>
        <div className="row g-3 mb-4">
          {gauge('CPU', m?.cpu_usage_percent)}
          {gauge('Memory', m?.memory_usage_percent)}
          {gauge('Disk', m?.disk_usage_percent)}
          {gauge('Temp', m?.cpu_temperature, '°C')}
        </div>
        {m && (m.battery_percent != null || m.uptime_seconds != null) && (
          <div className="row g-3 mb-4">
            {m.battery_percent != null && gauge(`Battery${m.battery_charging ? ' (charging)' : ''}`, m.battery_percent)}
            {m.uptime_seconds != null && (
              <div className="col-6 col-md-3">
                <div className="card h-100 text-center"><div className="card-body">
                  <div className="fs-4 fw-bold text-info">{Math.floor(m.uptime_seconds / 3600)}h</div>
                  <div className="text-muted small">Uptime</div>
                </div></div>
              </div>
            )}
            {m.process_count != null && (
              <div className="col-6 col-md-3">
                <div className="card h-100 text-center"><div className="card-body">
                  <div className="fs-4 fw-bold text-secondary">{m.process_count}</div>
                  <div className="text-muted small">Processes</div>
                </div></div>
              </div>
            )}
          </div>
        )}

        {/* Live shell */}
        {showTerminal && (
          <div className="mb-4">
            <PtyTerminal
              serverId={Number(id)}
              ws={wsRef.current}
              wsConnected={wsConnected}
              hostname={ep.hostname || ep.name}
              fullscreen={terminalFullscreen}
              onFullscreenToggle={() => setTerminalFullscreen(v => !v)}
              onRegisterHandler={(fn) => { ptyHandlerRef.current = fn; }}
            />
          </div>
        )}

        <div className="row g-3">
          {/* Hardware inventory */}
          <div className="col-12 col-lg-7">
            <div className="card h-100">
              <div className="card-header"><i className="bi bi-cpu me-2"></i>Hardware &amp; System</div>
              <div className="card-body p-0">
                <table className="table table-sm mb-0">
                  <tbody>
                    {infoRows.filter(([, v]) => v != null && v !== '').map(([label, value]) => (
                      <tr key={label}>
                        <td className="text-muted" style={{ width: '40%' }}>{label}</td>
                        <td className="fw-medium">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Command history */}
          <div className="col-12 col-lg-5">
            <div className="card h-100">
              <div className="card-header"><i className="bi bi-terminal me-2"></i>Recent Actions</div>
              <div className="card-body p-0" style={{ maxHeight: 400, overflowY: 'auto' }}>
                {commands.length === 0 ? (
                  <div className="text-center text-muted py-4">No commands run yet</div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {commands.map(c => {
                      const badge = c.status === 'completed' ? 'bg-success' : c.status === 'failed' ? 'bg-danger' : c.status === 'running' ? 'bg-info' : 'bg-secondary';
                      return (
                        <li className="list-group-item" key={c.id}>
                          <div className="d-flex justify-content-between">
                            <span className="fw-medium text-capitalize">{c.command_type.replace(/_/g, ' ')}</span>
                            <span className={`badge ${badge}`}>{c.status}</span>
                          </div>
                          <div className="small text-muted">{new Date(c.created_at).toLocaleString()}</div>
                          {c.result && <div className="small text-truncate" style={{ maxWidth: '100%' }}>{c.result}</div>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Security: threats + scans */}
        <div className="row g-3 mt-1">
          <div className="col-12 col-lg-7">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span><i className="bi bi-shield-exclamation me-2"></i>Threats</span>
                <button className="btn btn-sm btn-outline-primary" disabled={!online}
                  onClick={() => runCommand('run_scan', 'Scan')}>
                  <i className="bi bi-search me-1"></i>Scan now
                </button>
              </div>
              <div className="card-body p-0">
                {threats.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-shield-check text-success fs-4 d-block mb-2"></i>
                    No open threats
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm mb-0 align-middle">
                      <thead><tr>
                        <th>File</th><th>Signature</th><th>Severity</th><th>Source</th><th className="text-end">Actions</th>
                      </tr></thead>
                      <tbody>
                        {threats.map(t => {
                          const sevBadge = t.severity === 'critical' ? 'bg-danger' : t.severity === 'high' ? 'bg-warning text-dark' : t.severity === 'medium' ? 'bg-info text-dark' : 'bg-secondary';
                          return (
                            <tr key={t.id}>
                              <td className="text-truncate" style={{ maxWidth: 220 }} title={t.file_path}>
                                <code className="small">{t.file_path}</code>
                              </td>
                              <td className="small">{t.signature_name || '—'}</td>
                              <td><span className={`badge ${sevBadge}`}>{t.severity}</span></td>
                              <td className="small text-capitalize">{t.source}{t.action === 'quarantined' ? ' · quarantined' : ''}</td>
                              <td className="text-end">
                                <div className="btn-group btn-group-sm">
                                  {t.action !== 'quarantined' && (
                                    <button className="btn btn-outline-warning" title="Quarantine"
                                      onClick={() => actOnThreat(t.id, 'resolved', 'quarantine', 'Quarantine queued')}>
                                      <i className="bi bi-box-arrow-in-down"></i>
                                    </button>
                                  )}
                                  <button className="btn btn-outline-danger" title="Delete"
                                    onClick={() => actOnThreat(t.id, 'resolved', 'delete', 'Delete queued')}>
                                    <i className="bi bi-trash"></i>
                                  </button>
                                  <button className="btn btn-outline-secondary" title="Mark false positive"
                                    onClick={() => actOnThreat(t.id, 'false_positive', undefined, 'Marked false positive')}>
                                    <i className="bi bi-check2"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-5">
            <div className="card h-100">
              <div className="card-header"><i className="bi bi-clock-history me-2"></i>Scan History</div>
              <div className="card-body p-0" style={{ maxHeight: 400, overflowY: 'auto' }}>
                {scans.length === 0 ? (
                  <div className="text-center text-muted py-4">No scans yet</div>
                ) : (
                  <ul className="list-group list-group-flush">
                    {scans.map(s => (
                      <li className="list-group-item" key={s.id}>
                        <div className="d-flex justify-content-between">
                          <span className="fw-medium text-capitalize">{s.scan_type} scan <span className="text-muted small">({s.trigger.replace(/_/g, ' ')})</span></span>
                          <span className={`badge ${s.threats_found > 0 ? 'bg-danger' : 'bg-success'}`}>
                            {s.threats_found} threat{s.threats_found === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div className="small text-muted">
                          {s.files_scanned.toLocaleString()} files · {new Date(s.completed_at || s.created_at).toLocaleString()}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Phase 3: usage telemetry */}
        <div className="row g-3 mt-1">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span><i className="bi bi-bar-chart me-2"></i>App &amp; Usage (last 7 days)</span>
                <span className="small text-muted">
                  {usageEnabled ? `Total tracked: ${fmtDuration(usage?.total_seconds || 0)}` : 'Usage tracking is off'}
                </span>
              </div>
              <div className="card-body">
                {!usageEnabled ? (
                  <div className="text-muted small mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Usage tracking is disabled. Enable it in <a href="/endpoints/policy">Endpoint Policy</a> to see which apps and sites are used.
                    Note: this is employee-monitoring data — make sure your team is informed where required.
                  </div>
                ) : !usage || (usage.top_apps.length === 0 && usage.top_sites.length === 0) ? (
                  <div className="text-muted small mb-0">No usage recorded yet. Data appears after the agent samples activity.</div>
                ) : (
                  <div className="row g-4">
                    <div className="col-md-6">
                      <h6 className="text-muted text-uppercase small mb-3">Top Apps</h6>
                      {usage.top_apps.map((a, i) => {
                        const pct = usage.total_seconds ? Math.round((a.seconds / usage.total_seconds) * 100) : 0;
                        return (
                          <div key={i} className="mb-2">
                            <div className="d-flex justify-content-between small">
                              <span>{a.app_name}</span>
                              <span className="text-muted">{fmtDuration(a.seconds)}</span>
                            </div>
                            <div className="progress" style={{ height: 6 }}>
                              <div className="progress-bar bg-primary" style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted text-uppercase small mb-3">Top Sites / Categories</h6>
                      {usage.top_sites.length === 0 ? (
                        <div className="text-muted small">URL capture is off — only app-level data is collected.</div>
                      ) : usage.top_sites.map((s, i) => {
                        const pct = usage.total_seconds ? Math.round((s.seconds / usage.total_seconds) * 100) : 0;
                        return (
                          <div key={i} className="mb-2">
                            <div className="d-flex justify-content-between small">
                              <span>{s.site}</span>
                              <span className="text-muted">{fmtDuration(s.seconds)}</span>
                            </div>
                            <div className="progress" style={{ height: 6 }}>
                              <div className="progress-bar bg-info" style={{ width: `${pct}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EndpointDetailPage;
